import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { getUserEmail } from "../../../helpers/authUser";
import { updateDashboardFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from '../../../theme';
import { Dashboard, DashboardNote, DashboardNoteType, RefreshDataEnum } from "../../../types/files";
import { NotesContent } from "../../modals/dashboard-notes/DashboardNoteDialog";
import { CommentaryTitle } from "../dashboard-notes/Commentary";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 450,
        maxWidth: 500,
        height: 'fit-content',
        padding: 16,
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: 'all 300ms ease-in-out',
    },
    container: {
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 8,
        borderRadius: 12,
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#0f172a',
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > textarea": {
                fontFamily: 'Lato',
                fontSize: '0.9rem',
                color: '#475569',
                padding: 'unset',
            },
        },
    },
    icon: {
        width: 18,
        height: 18,
        padding: 'unset',
        paddingRight: 16,
        "& > .MuiSvgIcon-root": {
            fill: '#64748b',
        }
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff !important',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const CommentaryContentCard: React.FC<{
    dashboard: Dashboard,
    newCommentary?: boolean,
    onClose: () => void,
}> = ({ dashboard, newCommentary, onClose }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { isPublicView } = useContext(DashboardContext);

    const [editable, setEditable] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const inputValue = useRef<TextFieldProps>(null);

    const commentaryNote = useMemo(() => dashboard!.notes?.find(note => note.title === CommentaryTitle), [dashboard]);
    const commentaryNoteContent = useMemo(() => (JSON.parse(commentaryNote?.data || '{}') as NotesContent[])?.[1]?.data?.text || '', [commentaryNote]);

    const formattedContent = useMemo(() => {
        if (!commentaryNoteContent) return null;

        // Match labels only at start of string or after newline, followed by ":"
        const labelRegex = /(^|\n)(Commentary|Context|Decision):\s*/gi;
        const matches: RegExpExecArray[] = [];
        let match: RegExpExecArray | null;

        while ((match = labelRegex.exec(commentaryNoteContent)) !== null) {
            matches.push(match);
        }

        // No labels found at start of lines, return plain text
        if (matches.length === 0) {
            return { isPlainText: true, text: commentaryNoteContent };
        }

        const sections: { label: string; text: string }[] = [];

        for (let i = 0; i < matches.length; i++) {
            const m = matches[i];
            const label = m[2]; // The captured label (Commentary|Context|Decision)
            const startOfText = m.index + m[0].length;
            const endOfText = i < matches.length - 1 ? matches[i + 1].index : commentaryNoteContent.length;
            const text = commentaryNoteContent.slice(startOfText, endOfText).trim();
            sections.push({ label, text });
        }

        return { isPlainText: false, sections };
    }, [commentaryNoteContent]);

    const handleSave = useCallback(async (commentary: string) => {
        setLoading(true);

        const noteData = JSON.stringify([
            { type: 'header', data: { level: 2, text: CommentaryTitle, } },
            { type: 'paragraph', data: { text: commentary } },
        ]);
        const newNote: DashboardNote = {
            id: uuidv4(),
            title: CommentaryTitle,
            data: noteData,
            type: DashboardNoteType.External,
            createdBy: getUserEmail(user),
            updatedBy: getUserEmail(user),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        let oldNotes: DashboardNote[] = dashboard!.notes || [];
        let newNotes: DashboardNote[] = [];

        if (commentaryNote) {
            newNotes = oldNotes.map(note => {
                if (note.id === commentaryNote.id)
                    return newNote;
                return note;
            }) || [];
        } else {
            newNotes = [...oldNotes, newNote];
        }

        const dashboardUpdate: Dashboard = {
            ...dashboard!,
            notes: newNotes,
            refreshData: {
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Notes,
            }
        };

        updateDashboardFunc(dashboardUpdate).then(() => {
            setDashboards(prev => prev.map(d => d.id === dashboard.id ? dashboardUpdate : d));
            setLoading(false);
            setSaveReady(false);
            setEditable(false);
        });
        // eslint-disable-next-line
    }, [user, dashboard, commentaryNote]);

    useEffect(() => {
        if (newCommentary) {
            setEditable(true);
            setSaveReady(true);
        }
        // eslint-disable-next-line
    }, []);

    return (<>
        <Stack direction="column" className={classes.card} spacing={2} justifyContent="stretch">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
                <Typography className={classes.title}>
                    {`Context`}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-end">
                    {(!editable && !isPublicView) && (
                        <IconButton className={classes.icon} disableRipple
                            onClick={() => setEditable(true)}>
                            <EditOutlinedIcon />
                        </IconButton>
                    )}
                    <IconButton className={classes.icon} disableRipple
                        onClick={() => onClose()}>
                        <CloseIcon fontSize="small" sx={{ color: '#64748b' }} />
                    </IconButton>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" width="100%">
                <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                    <Stack className={classes.container} width="100%"
                        sx={editable ? { border: `1px solid ${theme.colors.neutral['300']}` } : {}}>
                        {editable ? (
                            <TextField variant="outlined"
                                className={classNames('no-border', classes.inputField)}
                                inputProps={{ style: { width: '100%' }, className: 'no-fade' }}
                                defaultValue={commentaryNoteContent}
                                placeholder={!isPublicView ? 'Add your comments here...' : ''}
                                onChange={() => setSaveReady(!!inputValue.current?.value)}
                                inputRef={inputValue}
                                disabled={isPublicView}
                                minRows={3}
                                maxRows={7}
                                multiline
                            />
                        ) : (
                            <Box sx={{ fontFamily: 'Lato', fontSize: '0.9rem', color: '#475569' }}>
                                {formattedContent ? (
                                    formattedContent.isPlainText ? (
                                        <Typography sx={{ fontFamily: 'Lato', fontSize: '0.9rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
                                            {formattedContent.text}
                                        </Typography>
                                    ) : (
                                        formattedContent.sections?.map((section: { label: string; text: string }, index: number) => (
                                            <Box key={index} sx={{ mb: index < (formattedContent.sections?.length ?? 0) - 1 ? 1.5 : 0 }}>
                                                <Typography component="span" sx={{ fontWeight: 700, fontFamily: 'Lato', fontSize: '0.9rem', color: '#475569' }}>
                                                    {section.label}:
                                                </Typography>
                                                <Typography component="div" sx={{ fontFamily: 'Lato', fontSize: '0.9rem', color: '#475569', mt: 0.5 }}>
                                                    {section.text || 'none'}
                                                </Typography>
                                            </Box>
                                        ))
                                    )
                                ) : (
                                    <Typography sx={{ fontFamily: 'Lato', fontSize: '0.9rem', color: '#9ca3af' }}>
                                        {!isPublicView ? 'Add your comments here...' : ''}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Stack>
                    {editable && (<>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%">
                            <Button variant="outlined" size="small"
                                className={classes.cancelButton}
                                onClick={() => setEditable(false)}
                            > {'Cancel'} </Button>
                            <LoadingButton variant="contained" size="small"
                                className={classes.saveButton}
                                loading={loading}
                                disabled={!saveReady}
                                onClick={() => handleSave?.(inputValue.current?.value as string)}
                            > {'Save'} </LoadingButton>
                        </Stack>
                    </>)}
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default memo(CommentaryContentCard);
